import { useState, useMemo, useCallback } from 'react';
import { useChat } from '../chat-provider';
import { cn } from '@/lib/utils';
import {
    MessageSquare, Plus, Trash2, Pencil, Check, X,
    Search, Pin, PinOff, Download, Share2,
} from 'lucide-react';
import { toast } from 'sonner';

function formatRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDays = Math.floor(diffHr / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface PinnedState {
    [conversationId: string]: string; // pinnedAt ISO string
}

export function ChatHistoryPanel() {
    const {
        conversations,
        activeConversationId,
        setActiveConversation,
        deleteConversation,
        renameConversation,
    } = useChat();

    const [searchQuery, setSearchQuery] = useState('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [pinned, setPinned] = useState<PinnedState>({});

    const handleNewChat = () => setActiveConversation(null);

    const handleRename = (id: string, currentTitle: string) => {
        setEditingId(id);
        setEditTitle(currentTitle);
    };

    const confirmRename = () => {
        if (editingId && editTitle.trim()) {
            renameConversation(editingId, editTitle.trim());
        }
        setEditingId(null);
    };

    const cancelRename = () => {
        setEditingId(null);
        setEditTitle('');
    };

    const togglePin = useCallback((id: string) => {
        setPinned((prev) => {
            const next = { ...prev };
            if (next[id]) {
                delete next[id];
            } else {
                next[id] = new Date().toISOString();
            }
            return next;
        });
    }, []);

    const handleExport = useCallback((id: string, title: string) => {
        toast.success(`Exported "${title}"`);
    }, []);

    const handleShare = useCallback((id: string) => {
        const url = `${window.location.origin}/chat/${id}`;
        navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
    }, []);

    const filtered = useMemo(() => {
        if (!searchQuery.trim()) return conversations;
        const q = searchQuery.toLowerCase();
        return conversations.filter((c) => c.title.toLowerCase().includes(q));
    }, [conversations, searchQuery]);

    const pinnedConvs = useMemo(
        () => filtered.filter((c) => pinned[c.id]).sort((a, b) => pinned[b.id].localeCompare(pinned[a.id])),
        [filtered, pinned],
    );

    const unpinnedConvs = useMemo(
        () => filtered.filter((c) => !pinned[c.id]),
        [filtered, pinned],
    );

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    History
                </h2>
                <button
                    onClick={handleNewChat}
                    className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
                    title="New conversation"
                >
                    <Plus className="h-4 w-4" />
                </button>
            </div>

            <div className="px-4 py-2">
                <div className="relative">
                    <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search conversations..."
                        className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-8 pr-8 text-xs text-slate-700 placeholder-slate-400 focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-2">
                {pinnedConvs.length > 0 && (
                    <div className="mb-2">
                        <p className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                            Pinned
                        </p>
                        {pinnedConvs.map((conv) => (
                            <ConversationItem
                                key={conv.id}
                                conv={conv}
                                isActive={conv.id === activeConversationId}
                                isPinned={true}
                                isEditing={editingId === conv.id}
                                editTitle={editTitle}
                                onSelect={() => editingId !== conv.id && setActiveConversation(conv.id)}
                                onRename={() => handleRename(conv.id, conv.title)}
                                onConfirmRename={confirmRename}
                                onCancelRename={cancelRename}
                                onEditTitleChange={setEditTitle}
                                onTogglePin={() => togglePin(conv.id)}
                                onExport={() => handleExport(conv.id, conv.title)}
                                onShare={() => handleShare(conv.id)}
                                onDelete={() => deleteConversation(conv.id)}
                            />
                        ))}
                    </div>
                )}

                {unpinnedConvs.length > 0 && (
                    <div>
                        {pinnedConvs.length > 0 && (
                            <p className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                Recent
                            </p>
                        )}
                        {unpinnedConvs.map((conv) => (
                            <ConversationItem
                                key={conv.id}
                                conv={conv}
                                isActive={conv.id === activeConversationId}
                                isPinned={false}
                                isEditing={editingId === conv.id}
                                editTitle={editTitle}
                                onSelect={() => editingId !== conv.id && setActiveConversation(conv.id)}
                                onRename={() => handleRename(conv.id, conv.title)}
                                onConfirmRename={confirmRename}
                                onCancelRename={cancelRename}
                                onEditTitleChange={setEditTitle}
                                onTogglePin={() => togglePin(conv.id)}
                                onExport={() => handleExport(conv.id, conv.title)}
                                onShare={() => handleShare(conv.id)}
                                onDelete={() => deleteConversation(conv.id)}
                            />
                        ))}
                    </div>
                )}

                {filtered.length === 0 && (
                    <div className="px-3 py-8 text-center text-xs text-slate-400">
                        {searchQuery ? 'No matching conversations' : 'No conversations yet'}
                    </div>
                )}
            </div>
        </div>
    );
}

interface ConversationItemProps {
    conv: { id: string; title: string; modified: string };
    isActive: boolean;
    isPinned: boolean;
    isEditing: boolean;
    editTitle: string;
    onSelect: () => void;
    onRename: () => void;
    onConfirmRename: () => void;
    onCancelRename: () => void;
    onEditTitleChange: (title: string) => void;
    onTogglePin: () => void;
    onExport: () => void;
    onShare: () => void;
    onDelete: () => void;
}

function ConversationItem({
    conv, isActive, isPinned, isEditing, editTitle,
    onSelect, onRename, onConfirmRename, onCancelRename, onEditTitleChange,
    onTogglePin, onExport, onShare, onDelete,
}: ConversationItemProps) {
    if (isEditing) {
        return (
            <div className="flex items-center gap-1 rounded-md px-3 py-2">
                <input
                    className="flex-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800"
                    value={editTitle}
                    onChange={(e) => onEditTitleChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onConfirmRename();
                        if (e.key === 'Escape') onCancelRename();
                    }}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                />
                <button onClick={(e) => { e.stopPropagation(); onConfirmRename(); }} className="text-slate-400 hover:text-slate-600">
                    <Check className="h-3 w-3" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); onCancelRename(); }} className="text-slate-400 hover:text-slate-600">
                    <X className="h-3 w-3" />
                </button>
            </div>
        );
    }

    return (
        <div
            className={cn(
                'group flex w-full cursor-pointer items-start gap-2 rounded-md px-3 py-2 text-left transition-colors',
                isActive
                    ? 'bg-blue-50 text-slate-900 dark:bg-blue-950 dark:text-slate-100'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100',
            )}
            onClick={onSelect}
        >
            <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">{conv.title}</p>
                <p className="mt-0.5 text-[10px] text-slate-400">{formatRelativeTime(conv.modified)}</p>
            </div>
            <div className="mt-0.5 flex shrink-0 items-center gap-0.5">
                <button
                    onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
                    className={cn(
                        'h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700',
                        isPinned ? 'flex' : 'hidden group-hover:flex',
                    )}
                    title={isPinned ? 'Unpin' : 'Pin'}
                >
                    {isPinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                </button>
                <button
                    onClick={(e) => { e.stopPropagation(); onRename(); }}
                    className="hidden h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 group-hover:flex dark:hover:bg-slate-700"
                    title="Rename"
                >
                    <Pencil className="h-3 w-3" />
                </button>
                <button
                    onClick={(e) => { e.stopPropagation(); onExport(); }}
                    className="hidden h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 group-hover:flex dark:hover:bg-slate-700"
                    title="Export"
                >
                    <Download className="h-3 w-3" />
                </button>
                <button
                    onClick={(e) => { e.stopPropagation(); onShare(); }}
                    className="hidden h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 group-hover:flex dark:hover:bg-slate-700"
                    title="Share"
                >
                    <Share2 className="h-3 w-3" />
                </button>
                <button
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="hidden h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-500 group-hover:flex dark:hover:bg-red-950"
                    title="Delete"
                >
                    <Trash2 className="h-3 w-3" />
                </button>
            </div>
        </div>
    );
}

export { ChatHistoryPanel as ChatSidebar };
